import { PolicyDefinition } from "./policyTypes";

export const polCore: PolicyDefinition[] = [
  {
    id: "core-statement",
    code: "CORE-001",
    title: "Statement of Purpose and Registration Compliance",
    category: "core",
    cqcFocus: "Well led",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Sets out who we are, the care we provide and how we meet the requirements of our registration with the CQC.",
    keyPoints: [
      "The Statement of Purpose describes the home, its residents and the care offered.",
      "Any change to registration details must be notified to the CQC in time.",
      "The statement is reviewed annually and after any major change.",
      "Admissions must match the needs described in the statement.",
    ],
    relatedRegulations: [
      "Health and Social Care Act 2008 (Regulated Activities) Regulations 2014",
      "CQC Registration Regulations 2009",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Keep the Statement of Purpose available in the home and on the admin system at all times.",
        "2. Review the statement every 12 months or whenever services change.",
        "3. Notify CQC of any change to the statement or registration within the legal timescale.",
        "4. Brief all staff on statement changes at the next meeting or supervision.",
        "5. Accept admissions only where needs match the statement and staffing capability.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Registered Manager owns the statement and its accuracy.",
        "Deputy Manager circulates updates to all staff.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual review recorded in the governance log.",
        "CQC notification register checked quarterly.",
      ]},
    ],
  },
  {
    id: "core-records",
    code: "CORE-002",
    title: "Care Records and Record Keeping",
    category: "core",
    cqcFocus: "Caring",
    sop: true,
    version: "4.0",
    reviewMonths: 12,
    summary: "Ensures care records are accurate, written at the time of care, respectful, secure and retained correctly.",
    keyPoints: [
      "Records are written at the time of care and signed with name and role.",
      "Corrections use a single line, initials and date; the original stays readable.",
      "Records never contain blanks, offensive language or personal opinions.",
      "Records are stored securely and only accessed by authorised people.",
    ],
    relatedRegulations: [
      "Regulation 17 Good Governance",
      "UK GDPR and Data Protection Act 2018",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Record care and observations at the time they happen.",
        "2. Use only approved forms and templates.",
        "3. Sign every entry with full name, role and date.",
        "4. Correct errors with a single line, initials and date; never use correction fluid.",
        "5. Write facts, not opinions; describe behaviour and actions taken.",
        "6. Store paper records in locked cabinets and digital records behind permissions.",
        "7. Report lost or missing records to the manager the same day.",
        "8. Forward subject access requests to the manager without delay.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Every staff member is responsible for their own entries.",
        "Registered Manager audits a sample of records every month.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly record audit of at least 10 percent of active records.",
        "Audit results feed the action plan log.",
      ]},
    ],
  },
  {
    id: "core-consent",
    code: "CORE-003",
    title: "Consent, Mental Capacity and Best Interests",
    category: "core",
    cqcFocus: "Safe",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Ensures every decision starts from presumed capacity, supports the person to decide, and follows the Mental Capacity Act where a best interest decision is needed.",
    keyPoints: [
      "Every adult is presumed to have capacity unless assessed otherwise.",
      "Capacity is decided for one specific decision at one specific time.",
      "Best interest decisions involve the person, family and advocates.",
      "The least restrictive option must always be chosen.",
    ],
    relatedRegulations: [
      "Mental Capacity Act 2005",
      "Regulation 11 Need for Consent",
      "MCA Code of Practice",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Presume capacity for every decision.",
        "2. Give information in an accessible way and at the right time of day.",
        "3. Assess capacity for the specific decision, with two people for major decisions.",
        "4. Complete the best interest checklist where capacity is lacking.",
        "5. Involve family, friends or an advocate in every best interest decision.",
        "6. Instruct an IMCA where there is no one to represent the person.",
        "7. Choose the option that restricts the person least.",
        "8. Review capacity and best interest decisions regularly and after any change.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Assessing staff complete capacity forms with a second signer for major decisions.",
        "Registered Manager reviews all best interest records quarterly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Quarterly MCA file audit.",
        "Training compliance for MCA checked monthly in the training matrix.",
      ]},
    ],
  },
  {
    id: "core-confidentiality",
    code: "CORE-004",
    title: "Confidentiality and Data Protection",
    category: "core",
    cqcFocus: "Well led",
    sop: false,
    version: "3.0",
    reviewMonths: 12,
    summary: "Personal information is collected lawfully, shared only on a need to know basis and kept secure at all times.",
    keyPoints: [
      "Information is shared only with people who need it for care.",
      "Consent is sought before sharing, unless safeguarding overrides it.",
      "Desks, screens and cabinets are kept locked and clear.",
      "Any data breach is reported within 24 hours.",
    ],
    relatedRegulations: [
      "UK GDPR and Data Protection Act 2018",
      "Regulation 17 Good Governance",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Discuss residents only in appropriate settings, never in public areas.",
        "2. Share information with outside bodies only with consent or a legal basis.",
        "3. Keep documents, screens and cabinets secure at all times.",
        "4. Use strong passwords and lock workstations when away.",
        "5. Report any breach or near miss to the manager within 24 hours.",
        "6. Shred personal data that is no longer needed.",
        "7. Return all documents and devices when leaving employment.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "All staff follow confidentiality rules at all times.",
        "Registered Manager acts as data protection lead and reports serious breaches to the ICO within 72 hours.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Annual data protection refresher for all staff.",
        "Quarterly spot checks of cabinets, screens and passwords.",
      ]},
    ],
  },
  {
    id: "core-candour",
    code: "CORE-005",
    title: "Duty of Candour and Openness",
    category: "core",
    cqcFocus: "Well led",
    sop: false,
    version: "2.0",
    reviewMonths: 12,
    summary: "When something goes wrong we apologise, explain and support the person affected, and notify the CQC where required.",
    keyPoints: [
      "An apology is not an admission of liability.",
      "The person and family are told promptly what happened.",
      "Conversations and actions are recorded in full.",
      "Notifiable events are reported to CQC within timescales.",
    ],
    relatedRegulations: [
      "Regulation 20 Duty of Candour",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Tell the person and their family what has happened as soon as reasonably practicable.",
        "2. Offer a sincere apology.",
        "3. Explain the facts known at the time and what will happen next.",
        "4. Provide reasonable support to the person and family.",
        "5. Record the conversation and the support offered.",
        "6. Complete any CQC notification within the required timescale.",
        "7. Review the event and share learning with the team.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Registered Manager leads all candour conversations.",
        "Deputy Manager prepares and tracks CQC notifications.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Candour log reviewed quarterly.",
        "Every notifiable event tracked to closure.",
      ]},
    ],
  },
  {
    id: "core-complaints",
    code: "CORE-006",
    title: "Complaints and Representations",
    category: "core",
    cqcFocus: "Responsive",
    sop: true,
    version: "3.0",
    reviewMonths: 12,
    summary: "Complaints are welcomed, recorded, investigated fairly and answered within timescales, then used to improve the service.",
    keyPoints: [
      "Complaints are acknowledged within 2 working days.",
      "A full written response is issued within 28 days.",
      "No one is treated unfavourably for making a complaint.",
      "Trends are reviewed every quarter.",
    ],
    relatedRegulations: [
      "Regulation 16 Receipt and Handling of Complaints",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Record every complaint, including verbal ones, on the day it is received.",
        "2. Acknowledge in writing within 2 working days.",
        "3. Investigate fairly: interview staff, review records and evidence.",
        "4. Send a full written response within 28 days with outcome and actions.",
        "5. If the complainant remains unhappy, explain the route to the Ombudsman.",
        "6. Log learning and actions from every complaint.",
        "7. Review complaint trends at the quarterly meeting.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Any staff member may receive a complaint and must pass it to the manager the same day.",
        "Registered Manager owns the complaints process.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Quarterly complaints trend report.",
        "Annual audit of timescales and outcomes.",
      ]},
    ],
  },
  {
    id: "core-property",
    code: "CORE-007",
    title: "Residents Money and Property",
    category: "core",
    cqcFocus: "Safe",
    sop: true,
    version: "2.0",
    reviewMonths: 12,
    summary: "Protects resident money and property through accurate inventories, receipts, secure storage and regular reconciliation.",
    keyPoints: [
      "A signed property and valuables inventory is completed on admission.",
      "All money handled for a resident is recorded and receipted.",
      "Staff never borrow money from residents.",
      "The ledger is reconciled weekly.",
    ],
    relatedRegulations: [
      "Regulation 12 Safe Care and Treatment",
    ],
    sections: [
      { title: "Procedure", lines: [
        "1. Complete the property and valuables inventory on admission with two signatures.",
        "2. Record all money handled for a resident in the ledger and issue a receipt.",
        "3. Store money and valuables in the safe; two staff present for access.",
        "4. Reconcile the ledger every week.",
        "5. Report any discrepancy immediately to the manager.",
      ]},
      { title: "Roles and responsibilities", lines: [
        "Senior staff hold safe keys and witness access.",
        "Registered Manager audits the ledger monthly.",
      ]},
      { title: "Monitoring and audit", lines: [
        "Monthly ledger and safe audit.",
        "Random receipt spot checks.",
      ]},
    ],
  },
];